/**
 * @file Comparator - Composant de comparaison de véhicules
 * @description Permet aux utilisateurs de comparer plusieurs véhicules côte à côte
 *              avec navigation responsive (swipe mobile, flèches desktop)
 */

import { BREAKPOINTS } from '../../../../assets/js/breakpoints';
import { attachSwipeEvents, throttle } from '../../utils';

/* =============================================================================
   CONFIGURATION
   ============================================================================= */

/** Nombre minimum de véhicules pour activer la navigation sur mobile/tablette */
const MIN_ITEMS_MOBILE_TABLET = 2;

/** Nombre minimum de véhicules pour activer la navigation sur desktop */
const MIN_ITEMS_DESKTOP = 4;

/** Nombre de colonnes visibles selon la taille d'écran */
const VIEW_LENGTHS = {
  SMALL: 1,
  MEDIUM: 2,
  LARGE: 3,
};

/** Espacement entre les colonnes de véhicules (en pixels) */
const ITEM_GAP = 40;

/* =============================================================================
   UTILITAIRES - Détection responsive
   ============================================================================= */

/**
 * Vérifie si la largeur correspond à un écran desktop
 * @param {number} width - Largeur de l'écran en pixels
 * @returns {boolean} true si desktop
 */
const isDesktop = (width) => width > BREAKPOINTS.md;

/**
 * Détermine le nombre de colonnes visibles selon la largeur
 * @param {number} width - Largeur du conteneur en pixels
 * @returns {number} Nombre de colonnes (1, 2 ou 3)
 */
const getViewLength = (width) => {
  if (width <= BREAKPOINTS.xs) {
    return VIEW_LENGTHS.SMALL;
  }

  if (width <= BREAKPOINTS.md) {
    return VIEW_LENGTHS.MEDIUM;
  }

  return VIEW_LENGTHS.LARGE;
};

/**
 * Vérifie si la navigation doit être initialisée
 * @param {number} totalItems - Nombre total de véhicules à comparer
 * @param {number} width - Largeur du conteneur
 * @returns {boolean} true si la navigation est nécessaire
 */
const shouldInitializeNavigation = (totalItems, width) => {
  if (isDesktop(width)) {
    return totalItems >= MIN_ITEMS_DESKTOP;
  }

  return totalItems >= MIN_ITEMS_MOBILE_TABLET;
};

/* =============================================================================
   SECTIONS DÉPLIABLES - Gestion des accordéons
   ============================================================================= */

/**
 * Met à jour l'attribut aria-expanded d'un trigger
 * @param {HTMLElement} trigger - Élément déclencheur
 * @param {boolean} isOpen - État ouvert/fermé
 */
const updateAriaExpanded = (trigger, isOpen) => {
  trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
};

/**
 * Initialise les toggles d'ouverture/fermeture des sections
 * @param {NodeList} sections - Liste des sections dépliables
 */
const initSectionToggles = (sections) => {
  for (const section of sections) {
    const trigger = section.querySelector('.js-comparator-sectionTitle');

    if (trigger) {
      const toggleSection = () => {
        section.classList.toggle('-open');
        updateAriaExpanded(trigger, section.classList.contains('-open'));
      };

      trigger.addEventListener('click', toggleSection);

      // Support clavier (Enter/Space)
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleSection();
        }
      });
    }
  }
};

/* =============================================================================
   CALCULS DE LAYOUT - Dimensionnement des colonnes
   ============================================================================= */

/**
 * Calcule la largeur d'une colonne de véhicule
 * @param {number} containerWidth - Largeur totale du conteneur
 * @param {number} viewLength - Nombre de colonnes visibles
 * @returns {number} Largeur d'une colonne en pixels
 */
const calculateItemWidth = (containerWidth, viewLength) => {
  if (viewLength === VIEW_LENGTHS.SMALL) {
    return containerWidth;
  }

  return (containerWidth - viewLength * ITEM_GAP) / (viewLength + 1);
};

/**
 * Met à jour la direction flex des conteneurs de liste
 * @param {NodeList} containers - Conteneurs de liste
 * @param {number} viewLength - Nombre de colonnes visibles
 */
const updateListContainersLayout = (containers, viewLength) => {
  const isMultiColumn = viewLength > VIEW_LENGTHS.SMALL;

  for (const container of containers) {
    container.style.flexDirection = isMultiColumn ? 'row' : 'column';
    container.style.gap = isMultiColumn ? `${ITEM_GAP}px` : '0';
  }
};

/**
 * Applique la largeur calculée aux items
 * @param {NodeList} items - Éléments véhicule
 * @param {number} width - Largeur à appliquer
 */
const updateItemWidths = (items, width) => {
  for (const item of items) {
    item.style.flex = `0 0 ${width}px`;
  }
};

/**
 * Applique la largeur aux clés de section (labels)
 * @param {NodeList} keys - Éléments clé/label
 * @param {number} width - Largeur à appliquer
 * @param {number} viewLength - Nombre de colonnes visibles
 */
const updateKeyWidths = (keys, width, viewLength) => {
  const flexBasis = viewLength > VIEW_LENGTHS.SMALL ? `${width}px` : 'unset';

  for (const key of keys) {
    key.style.flexBasis = flexBasis;
  }
};

/* =============================================================================
   NAVIGATION - Défilement entre véhicules
   ============================================================================= */

/**
 * Applique la transformation de défilement horizontal aux items
 * @param {NodeList} items - Éléments à déplacer
 * @param {number} delta - Décalage en nombre d'items
 * @param {number} width - Largeur d'un item
 */
const updateItemsTransform = (items, delta, width) => {
  const translateX = delta * (width + ITEM_GAP);

  for (const item of items) {
    item.style.transform = `translateX(-${translateX}px)`;
  }
};

/**
 * Met à jour l'indicateur visuel de l'item actif dans le header
 * @param {NodeList} headerItems - Items du header
 * @param {number} currentIndex - Index actuel (1-based)
 */
const updateActiveHeaderItem = (headerItems, currentIndex) => {
  headerItems.forEach((item, index) => {
    item.classList.toggle('-active', index + 1 === currentIndex);
  });
};

/**
 * Met à jour l'état des boutons prev/next (désactivé aux extrémités)
 * @param {HTMLElement} prevTrigger - Bouton précédent
 * @param {HTMLElement} nextTrigger - Bouton suivant
 * @param {number} currentIndex - Index actuel (1-based)
 * @param {number} totalLength - Nombre total d'items
 */
const updateNavigationState = (prevTrigger, nextTrigger, currentIndex, totalLength) => {
  prevTrigger.classList.toggle('-disabled', currentIndex === 1);
  nextTrigger.classList.toggle('-disabled', currentIndex === totalLength);
};

/* =============================================================================
   STICKY DETECTION - Détection de l'état sticky du header
   ============================================================================= */

/**
 * Initialise la détection de l'état sticky du header
 * Utilise un élément sentinelle et IntersectionObserver pour détecter l'état sticky
 * @param {HTMLElement} headerContainer - Conteneur du header à observer
 */
const initStickyDetection = (headerContainer) => {
  // Crée une sentinelle invisible placée juste avant le header sticky
  const sentinel = document.createElement('div');

  sentinel.className = 'js-comparator-stickySentinel';
  sentinel.style.height = '1px';
  sentinel.style.marginBottom = '-1px';
  headerContainer.parentNode.insertBefore(sentinel, headerContainer);

  // Récupère la hauteur du header principal
  const headerHeight = parseInt(
    getComputedStyle(document.documentElement).getPropertyValue('--header-height'),
    10,
  );

  // Quand la sentinelle sort du viewport (au-dessus du header principal), le header est sticky
  const observer = new IntersectionObserver(
    ([entry]) => {
      headerContainer.classList.toggle('-sticky', !entry.isIntersecting);
    },
    {
      rootMargin: `-${headerHeight}px 0px 0px 0px`,
      threshold: 0,
    },
  );

  observer.observe(sentinel);
};

/* =============================================================================
   INITIALISATION - Composant principal
   ============================================================================= */

/**
 * Initialise la navigation du comparateur (swipe + flèches)
 * @param {HTMLElement} comparator - Élément racine du comparateur
 * @param {number} totalItems - Nombre total de véhicules
 */
const initComparatorNavigation = (comparator, totalItems) => {
  comparator.classList.add('-init');

  // Éléments de navigation
  const prevTrigger = comparator.querySelector('.js-comparator-navPrev');
  const nextTrigger = comparator.querySelector('.js-comparator-navNext');
  const swipeableElements = comparator.querySelectorAll(
    '.js-comparator-tableHeaderContainer, .js-comparator-sections',
  );
  const counter = comparator.querySelector('.js-comparator-counterItem');
  const headerContainer = comparator.querySelector('.js-comparator-tableHeaderContainer');

  // Éléments à manipuler pour le layout
  const elements = {
    items: comparator.querySelectorAll('.js-comparator-tableHeaderItem, .js-comparator-sectionItem'),
    keys: comparator.querySelectorAll('.js-comparator-sectionItemKey'),
    headerItems: comparator.querySelectorAll('.js-comparator-tableHeaderItem'),
    listContainers: comparator.querySelectorAll('.js-comparator-sectionListContainer'),
  };

  // État de la navigation
  let currentIndex = 1;
  let viewLength = VIEW_LENGTHS.LARGE;

  /**
   * Recalcule et applique tout le layout
   */
  const updateView = () => {
    const containerWidth = comparator.clientWidth;
    const itemWidth = calculateItemWidth(containerWidth, viewLength);
    const delta = Math.min(currentIndex - 1, totalItems - viewLength);
    const isMultiColumn = viewLength > VIEW_LENGTHS.SMALL;

    updateListContainersLayout(elements.listContainers, viewLength);
    updateItemWidths(elements.items, itemWidth);
    updateKeyWidths(elements.keys, itemWidth, viewLength);
    updateItemsTransform(elements.items, delta, itemWidth);
    updateActiveHeaderItem(elements.headerItems, currentIndex);
    updateNavigationState(prevTrigger, nextTrigger, currentIndex, totalItems);

    headerContainer.style.paddingLeft = isMultiColumn ? `${itemWidth + ITEM_GAP}px` : '0';
    counter.textContent = currentIndex.toString().padStart(2, '0');
  };

  /**
   * Gestionnaire de redimensionnement
   */
  const handleResize = () => {
    viewLength = getViewLength(comparator.clientWidth);
    updateView();
  };

  /**
   * Passe au véhicule suivant
   */
  const goToNext = () => {
    if (currentIndex < totalItems) {
      currentIndex++;
      updateView();
    }
  };

  /**
   * Passe au véhicule précédent
   */
  const goToPrevious = () => {
    if (currentIndex > 1) {
      currentIndex--;
      updateView();
    }
  };

  // Attachement des événements swipe (mobile)
  for (const element of swipeableElements) {
    attachSwipeEvents(element, {
      onSwipeLeft: goToNext,
      onSwipeRight: goToPrevious,
    });
  }

  // Attachement des événements click et resize
  window.addEventListener('resize', throttle(handleResize, 100));
  prevTrigger.addEventListener('click', goToPrevious);
  nextTrigger.addEventListener('click', goToNext);

  // Premier rendu
  handleResize();
};

/**
 * Point d'entrée du composant Comparator
 * Initialise les sections dépliables et la navigation si nécessaire
 */
const Comparator = () => {
  const sections = document.querySelectorAll('.js-comparator-section');
  const comparator = document.querySelector('.js-comparator');
  const headerContainer = document.querySelector('.js-comparator-tableHeaderContainer');

  // Toujours initialiser les accordéons
  initSectionToggles(sections);

  // Initialiser la détection sticky du header
  if (headerContainer) {
    initStickyDetection(headerContainer);
  }

  if (!comparator) {
    return;
  }

  const totalItems = parseInt(comparator.dataset.length, 10);

  // Validation des données
  if (Number.isNaN(totalItems) || totalItems < 1) {
    console.warn('[Comparator] data-length invalide ou absent');

    return;
  }

  // Initialisation immédiate si le viewport le permet
  if (shouldInitializeNavigation(totalItems, comparator.clientWidth)) {
    initComparatorNavigation(comparator, totalItems);

    return;
  }

  // Sinon, attendre un éventuel redimensionnement
  const handleResizeForInit = () => {
    if (shouldInitializeNavigation(totalItems, comparator.clientWidth)) {
      initComparatorNavigation(comparator, totalItems);
      window.removeEventListener('resize', handleResizeForInit);
    }
  };

  window.addEventListener('resize', handleResizeForInit);
};

export default Comparator;
